import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { collection, doc, onSnapshot } from 'firebase/firestore';
import type { Evento, Invitacion, Mesa, MiembroEvento, Usuario } from '@eventos-cien/shared';
import { db } from '../lib/firebase';

export interface MesaConId extends Mesa {
  id: string;
}
export interface UsuarioConId extends Usuario {
  uid: string;
}
export interface MiembroConId extends MiembroEvento {
  uid: string;
}

interface AdminEventoData {
  cargando: boolean;
  eventoId: string;
  evento: Evento | null;
  invitaciones: Invitacion[];
  mesas: MesaConId[];
  miembros: MiembroConId[];
  /** Directorio global de usuarios (el admin puede leer todos). */
  usuarios: UsuarioConId[];
}

const Ctx = createContext<AdminEventoData | null>(null);

/**
 * Reemplaza a AdminData.tsx: todo el estado ahora vive bajo
 * eventos/{eventoId}/... en vez de colecciones planas. Se monta una vez por
 * cada visita a /admin/eventos/:eventoId (ver EventoDetalleLayout).
 */
export function AdminEventoDataProvider({
  eventoId,
  children,
}: {
  eventoId: string;
  children: ReactNode;
}) {
  const [evento, setEvento] = useState<Evento | null | undefined>(undefined);
  const [invitaciones, setInvitaciones] = useState<Invitacion[] | null>(null);
  const [mesas, setMesas] = useState<MesaConId[]>([]);
  const [miembros, setMiembros] = useState<MiembroConId[]>([]);
  const [usuarios, setUsuarios] = useState<UsuarioConId[]>([]);

  useEffect(() => {
    setEvento(undefined);
    setInvitaciones(null);
    return onSnapshot(doc(db, 'eventos', eventoId), (s) =>
      setEvento(s.exists() ? (s.data() as Evento) : null),
    );
  }, [eventoId]);

  useEffect(
    () =>
      onSnapshot(
        collection(db, 'eventos', eventoId, 'invitaciones'),
        (s) => setInvitaciones(s.docs.map((d) => d.data() as Invitacion)),
        () => setInvitaciones([]),
      ),
    [eventoId],
  );

  useEffect(
    () =>
      onSnapshot(collection(db, 'eventos', eventoId, 'mesas'), (s) =>
        setMesas(
          s.docs.map((d) => ({ ...(d.data() as Mesa), id: d.id })).sort((a, b) => a.numero - b.numero),
        ),
      ),
    [eventoId],
  );

  useEffect(
    () =>
      onSnapshot(collection(db, 'eventos', eventoId, 'miembros'), (s) =>
        setMiembros(s.docs.map((d) => ({ ...(d.data() as MiembroEvento), uid: d.id }))),
      ),
    [eventoId],
  );

  useEffect(
    () =>
      onSnapshot(collection(db, 'usuarios'), (s) =>
        setUsuarios(s.docs.map((d) => ({ ...(d.data() as Usuario), uid: d.id }))),
      ),
    [],
  );

  const value: AdminEventoData = {
    cargando: evento === undefined || invitaciones === null,
    eventoId,
    evento: evento ?? null,
    invitaciones: invitaciones ?? [],
    mesas,
    miembros,
    usuarios,
  };
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAdminEventoData(): AdminEventoData {
  const c = useContext(Ctx);
  if (!c) throw new Error('useAdminEventoData debe usarse dentro de <AdminEventoDataProvider>');
  return c;
}
